import React, { useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Home from './components/Home';
import Landing from './components/Landing';
import FAQ from './components/FAQ';
import ProductPage from './components/ProductPage';
import SuccessPage from './components/SuccessPage';
import ContactUs from './components/ContactUs';
import PrivacyPolicy from './components/PrivacyPolicy';
import EmailPreferencesPage from './components/EmailPreferencesPage';
import TermsAndConditions from './components/TermsAndConditions';
import PaymentFailed from './components/PaymentFailed';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import CookieBanner from './components/CookieBanner';
import Redownload from './components/Redownload';
import DownloadPage from './components/DownloadPage';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import UserDashboard from './components/UserDashboard';
import SignalDetailPage from './components/SignalDetailPage';
import SignalsMarketplacePage from './components/SignalsMarketplacePage';
import CreatorProfilePage from './components/CreatorProfilePage';
import LiveAccountPage from './components/LiveAccountPage';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import ProductForm from './components/ProductForm';
import PromoCodeForm from './components/PromoCodeForm';
import StrategyTester from './components/StrategyTester';
import Mql5ImportPage from './components/Mql5ImportPage';
import TesterPremiumPage from './components/TesterPremiumPage';
import CommunityPage from './components/CommunityPage';
import BuilderDocsPage from './components/BuilderDocsPage';
import StrategyArchitectPage from './components/StrategyArchitectPage';
import SiteSearchPage from './components/SiteSearchPage';
import './App.css';

function parseJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (error) {
    return null;
  }
}

function hasValidToken(storageKey) {
  const token = localStorage.getItem(storageKey);
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  if (payload.exp * 1000 <= Date.now()) {
    localStorage.removeItem(storageKey);
    window.dispatchEvent(new Event('auth-changed'));
    return false;
  }
  return true;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    const getVisitorId = () => {
      const storageKey = 'vix_visitor_id';
      const existing = localStorage.getItem(storageKey);
      if (existing) return existing;
      const generated = `vix:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(storageKey, generated);
      return generated;
    };

    const track = async () => {
      try {
        const userToken = localStorage.getItem('userToken');
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const payload = {
          path: `${location.pathname}${location.search}`,
          referrer: document.referrer || '',
          visitorId: getVisitorId(),
        };
        if (!userToken && navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          navigator.sendBeacon(`${apiBaseUrl}/api/analytics/track`, blob);
          return;
        }
        await axios.post(`${apiBaseUrl}/api/analytics/track`, payload, userToken ? { headers: { Authorization: `Bearer ${userToken}` } } : undefined);
      } catch (err) {
        // noop
      }
    };
    track();
  }, [location.pathname, location.search]);
  return null;
}

function ProtectedRoute({ children }) {
  return hasValidToken('adminToken') ? children : <Navigate to="/admin/login" replace />;
}

function UserProtectedRoute({ children }) {
  return hasValidToken('userToken') ? children : <Navigate to="/login" replace />;
}

function PublicOnlyRoute({ children }) {
  return hasValidToken('userToken') ? <Navigate to="/dashboard" replace /> : children;
}

function LoggedOutOnlyRoute({ children, redirectTo = '/dashboard' }) {
  return hasValidToken('userToken') ? <Navigate to={redirectTo} replace /> : children;
}

function LandingRoute() {
  return hasValidToken('userToken') ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function AdminPublicOnlyRoute({ children }) {
  return hasValidToken('adminToken') ? <Navigate to="/admin" replace /> : children;
}

function AppShell() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isTesterRoute = location.pathname === '/tester';

  return (
    <div className="app-container">
      <ScrollToTop />
      <AnalyticsTracker />
      {!isTesterRoute && <Navbar />}
      <Routes>
        <Route path="/" element={<LandingRoute />} />
        <Route path="/products" element={<Home />} />
        <Route path="/search" element={<SiteSearchPage />} />
        <Route path="/product/:id" element={<ProductPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/contact-us" element={<LoggedOutOnlyRoute redirectTo="/dashboard?tab=support"><ContactUs /></LoggedOutOnlyRoute>} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        {/* Reached from the footer of any marketing email. No login: most of the list has no account. */}
        <Route path="/email-preferences" element={<EmailPreferencesPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/payment-failed" element={<PaymentFailed />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/tester" element={<UserProtectedRoute><StrategyTester /></UserProtectedRoute>} />
        <Route path="/import-ea" element={<UserProtectedRoute><Mql5ImportPage /></UserProtectedRoute>} />
        <Route path="/tester-premium" element={<TesterPremiumPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/builder-docs" element={<BuilderDocsPage />} />
        <Route path="/vixai" element={<Navigate to="/tester?panel=ai" replace />} />
        <Route path="/strategy-architect" element={<StrategyArchitectPage />} />
        <Route path="/redownload" element={<LoggedOutOnlyRoute redirectTo="/dashboard?tab=purchases"><Redownload /></LoggedOutOnlyRoute>} />
        <Route path="/download/:id" element={<DownloadPage />} />
        <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
        <Route path="/signup" element={<PublicOnlyRoute><Signup /></PublicOnlyRoute>} />
        <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
        <Route path="/dashboard" element={<UserProtectedRoute><UserDashboard /></UserProtectedRoute>} />
        <Route path="/signals" element={<SignalsMarketplacePage />} />
        <Route path="/signals/creators/:userId" element={<CreatorProfilePage />} />
        <Route path="/signals/:id" element={<UserProtectedRoute><SignalDetailPage /></UserProtectedRoute>} />
        <Route path="/live/:id" element={<UserProtectedRoute><LiveAccountPage /></UserProtectedRoute>} />
        <Route path="/admin/login" element={<AdminPublicOnlyRoute><AdminLogin /></AdminPublicOnlyRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/add-product" element={<ProtectedRoute><ProductForm /></ProtectedRoute>} />
        <Route path="/admin/edit-product/:id" element={<ProtectedRoute><ProductForm /></ProtectedRoute>} />
        <Route path="/admin/add-promo" element={<ProtectedRoute><PromoCodeForm /></ProtectedRoute>} />
        <Route path="/admin/edit-promo/:id" element={<ProtectedRoute><PromoCodeForm /></ProtectedRoute>} />
      </Routes>
      {!isAdminRoute && !isTesterRoute && <Footer />}
      {!isAdminRoute && !isTesterRoute && <CookieBanner />}
      {!isAdminRoute && !isTesterRoute && <BackToTop />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
